/**
 * @file src/utils/pricing.js
 * @description Pure pricing optimization functions.
 *
 * Scans currency_pricing items for duplicate pack prices and sets redundant
 * tiers to -1 so the UI only shows the best-value option.
 *
 * Extracted from eventloader.js to eliminate duplicated pricing logic
 * (prelive vs live) and make the algorithm independently testable.
 */

/**
 * Scan pricing items and collect pack cost/location metadata.
 * @param {Array} items - currency_pricing[0].items array
 * @param {Function} filter - optional predicate to restrict to a subevent
 * @param {boolean} trackMovie - whether to track $movie product
 * @returns {Object} Pack pricing metadata
 */
function collectPackCosts(items, filter, trackMovie) {
  const costs = {
    event_cd_cost: 0,
    superpack_cost: 0,
    megapack_cost: 0,
    superpack_location: 0,
    event_cd_location: 0,
    baseback: false,
  }

  if (trackMovie) {
    costs.movie_location = 0
    costs.movie_price = 0
  }

  items.forEach((item, ind) => {
    if (filter && !filter(item, ind)) return

    if (item.product === 'dl_eventcd') {
      costs.event_cd_cost = item.price
      costs.event_cd_location = ind
      costs.baseback = true
    }
    if (item.product === 'dl_digsuperpack') {
      costs.superpack_cost = item.price
      costs.superpack_location = ind
    }
    if (item.product === 'dl_megapack') {
      costs.megapack_cost = item.price
    }
    if (trackMovie && item.product === '$movie') {
      costs.movie_location = ind
      costs.movie_price = item.price
    }
  })

  return costs
}

/**
 * Apply pack collapsing rules: set redundant tier prices to -1.
 *
 * Rules:
 * 1. All three equal → hide eventcd + superpack (+ movie if tracked)
 * 2. No base (or -1) and super === mega → hide superpack (+ movie)
 * 3. No mega (0 or -1) and base === super → hide eventcd (+ movie)
 *
 * @param {Array} items - currency_pricing[0].items (mutated in place)
 * @param {Object} costs - output from collectPackCosts
 */
function applyPackRules(items, costs) {
  const { event_cd_cost, superpack_cost, megapack_cost, baseback } = costs
  const { superpack_location, event_cd_location } = costs
  const trackMovie = 'movie_price' in costs
  const movie_price = trackMovie ? costs.movie_price : 0
  const movie_location = trackMovie ? costs.movie_location : 0

  if (
    event_cd_cost > 0 &&
    event_cd_cost === superpack_cost &&
    event_cd_cost === megapack_cost
  ) {
    items[event_cd_location].price = -1
    items[superpack_location].price = -1
    if (trackMovie && movie_price > 0) {
      items[movie_location].price = -1
    }
  } else if (
    (event_cd_cost === -1 || !baseback) &&
    superpack_cost > 0 &&
    superpack_cost === megapack_cost
  ) {
    items[superpack_location].price = -1
    if (trackMovie && movie_price > 0) {
      items[movie_location].price = -1
    }
  } else if (
    (megapack_cost === 0 || megapack_cost === -1) &&
    event_cd_cost > 0 &&
    event_cd_cost === superpack_cost
  ) {
    if (trackMovie && movie_price > 0) {
      items[movie_location].price = -1
    }
    items[event_cd_location].price = -1
  }
}

/**
 * Optimize pack pricing by collapsing redundant tiers to -1.
 *
 * Handles both global (no subevent) items and per-subevent items.
 * Mutates items array in place (matches original behaviour).
 *
 * @param {Array} items - currency_pricing[0].items
 * @param {Object} [options]
 * @param {boolean} [options.trackMovie=false] - include $movie in collapsing
 */
export function optimizePackPricing(items, options = {}) {
  const trackMovie = options.trackMovie === true

  // 1. Collect and apply rules for global (non-subevent) items
  const globalCosts = collectPackCosts(
    items,
    (item) => !item.subevent,
    trackMovie,
  )
  applyPackRules(items, globalCosts)

  // 2. Build unique subevent list
  const subeventNames = new Set()
  items.forEach((item) => {
    if (item.subevent) subeventNames.add(item.subevent)
  })

  // 3. Collect and apply rules per subevent
  subeventNames.forEach((subevent) => {
    const subCosts = collectPackCosts(
      items,
      (item) => item.subevent && item.subevent === subevent,
      trackMovie,
    )
    applyPackRules(items, subCosts)
  })
}
